import api from './client';
import type { ApiResponse } from '@gokrew/models/generic';
import type {
  GetHistoryParams,
  GetRequestsParams,
  GetWeekApprovalsParams,
  UpdateSegmentPayload,
  PatchDayPayload,
  CreateRequestPayload,
  WriteDayPayload,
  TimesheetStatus,
} from '@/types/timesheet';

/* ---------------------------------- */
/* 🔧 Endpoints */
/* ---------------------------------- */

const base = '/employee/timesheet';

const endpoints = {
  today: `${base}/today`,
  day: `${base}/day`,
  history: `${base}/history`,
  hoursSummary: `${base}/hours-summary`,
  requests: `${base}/requests`,
  weekApprovals: `${base}/week-approvals`,
  segment: (id: string) => `${base}/segments/${id}`,
  clockIn: `${base}/clock-in`,
  breakStart: `${base}/break/start`,
  breakEnd: `${base}/break/end`,
  clockOut: `${base}/clock-out`,
  submitDay: `${base}/days/submit`,
  policy: '/employee/timesheet-policy',
  status: '/employee/timesheet-status',
};

/* ---------------------------------- */
/* ⏱️ TIMESHEET */
/* ---------------------------------- */

export const getToday = async () => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { data } = await api.get(endpoints.today, { params: { timeZone } });
  return data;
};

export const getDay = async (date: string) => {
  const { data } = await api.get(endpoints.day, { params: { date } });
  return data;
};

export const getHistory = async (params: GetHistoryParams) => {
  const { data } = await api.get(endpoints.history, { params });
  return data;
};

export const getHoursSummary = async (params: GetHistoryParams) => {
  const { data } = await api.get(endpoints.hoursSummary, { params });
  return data;
};

export const getRequests = async (params: GetRequestsParams) => {
  const { data } = await api.get(endpoints.requests, { params });
  return data;
};

export const getWeekApprovals = async (params: GetWeekApprovalsParams) => {
  const { data } = await api.get(endpoints.weekApprovals, { params });
  return data;
};

export const updateSegment = async (id: string, payload: UpdateSegmentPayload) => {
  const { data } = await api.patch(endpoints.segment(id), payload);
  return data;
};

export const clockIn = async () => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { data } = await api.post(endpoints.clockIn, { timeZone });
  return data;
};

export const startBreak = async (employeeNote?: string) => {
  const { data } = await api.post(endpoints.breakStart, { employeeNote });
  return data;
};

export const endBreak = async (employeeNote?: string) => {
  const { data } = await api.post(endpoints.breakEnd, { employeeNote });
  return data;
};

export const clockOut = async (employeeNote?: string) => {
  const { data } = await api.post(endpoints.clockOut, { employeeNote });
  return data;
};

export const submitDay = async (date: string) => {
  const { data } = await api.post(endpoints.submitDay, undefined, { params: { date } });
  return data;
};

export const patchDay = async (date: string, payload: PatchDayPayload) => {
  const { data } = await api.patch(endpoints.day, payload, { params: { date } });
  return data;
};

export const createRequest = async (payload: CreateRequestPayload) => {
  const { data } = await api.post(endpoints.requests, payload);
  return data;
};

export const writeDay = async (payload: WriteDayPayload) => {
  const { data } = await api.put(endpoints.day, payload);
  return data;
};

export interface TimesheetPolicy {
  autoApprove: boolean;
  requestFlowEnabled: boolean;
}

export type TimesheetPolicyResponse = ApiResponse<TimesheetPolicy>;

export const getTimesheetPolicy = async (): Promise<TimesheetPolicyResponse> => {
  const { data } = await api.get<TimesheetPolicyResponse>(endpoints.policy);
  return data;
};

export const getTimesheetStatus = async (): Promise<ApiResponse<TimesheetStatus>> => {
  const { data } = await api.get<ApiResponse<TimesheetStatus>>(endpoints.status);
  return data;
};
